import { HttpClient, HttpContext } from '@angular/common/http';
import { inject } from '@angular/core';

import { catchError, of, throwError } from 'rxjs';
import {
  TOAST_CONTEXT,
  ToastContext,
} from '../../../core/interceptors/http-toast.interceptor';
import { environment } from '../../../../environments/environment';

export class ApiError extends Error {
  constructor(public response?: { statusCode: number; body: any }) {
    super('API Error');
    this.name = 'ApiError';
  }
}

interface PaginatedResponse<T> {
  items: T[];
  cursor?: string;
  limit?: number;
}

export class BaseCrudService<TGet, TCreate, TUpdate, TParam> {
  private endpoint: string;
  protected http = inject(HttpClient);
  constructor(endpoint: string) {
    this.endpoint = endpoint.endsWith('/') ? endpoint : endpoint + '/';
  }

  get(id?: TParam, params?: Record<string, any>) {
    const url = this.getUrl(id);
    return this.http
      .get<TGet>(url, {
        params,
      })
      .pipe(
        catchError((error) => {
          this.handleError(error);
          return of([] as TGet);
        })
      );
  }

  create(item: TCreate, toastContext?: ToastContext) {
    const url = this.getUrl();
    return this.http
      .post<TGet>(
        url,
        item,
        toastContext
          ? { context: new HttpContext().set(TOAST_CONTEXT, toastContext) }
          : undefined
      )
      .pipe(
        catchError((error) => {
          this.handleError(error);
          return throwError(() => error);
        })
      );
  }

  update(id: TParam, item: TUpdate, toastContext?: ToastContext) {
    const url = this.getUrl(id);
    return this.http
      .put<TGet>(
        url,
        item,
        toastContext
          ? { context: new HttpContext().set(TOAST_CONTEXT, toastContext) }
          : undefined
      )
      .pipe(
        catchError((error) => {
          this.handleError(error);
          return of(null);
        })
      );
  }

  delete(id: TParam, toastContext?: ToastContext) {
    const url = this.getUrl(id);
    return this.http
      .delete<TGet>(
        url,
        toastContext
          ? { context: new HttpContext().set(TOAST_CONTEXT, toastContext) }
          : undefined
      )
      .pipe(
        catchError((error) => {
          this.handleError(error);
          return of(null);
        })
      );
  }

  private handleError(error: any) {
    if (error instanceof ApiError) {
      if (error.response) {
        const { statusCode, body } = error.response;
        console.error(
          `Received ${statusCode} error response with payload: ${body}`
        );
      } else {
        console.error(`Received unknown error response with payload: ${error}`);
      }
    } else {
      console.error('Unexpected error: ', error);
    }
  }

  private getUrl(id?: TParam) {
    let url = environment.baseApiUrl + this.endpoint;
    if (id) {
      url += id;
    }
    return url;
  }
}
